import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { ToastContainer } from '../components/common/ToastContainer';
import { APP_ERROR_EVENT } from '../utils/appEvents';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const DEFAULT_DURATIONS: Record<ToastType, number> = {
  success: 3500,
  info: 3500,
  warning: 5000,
  error: 6000,
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counterRef = useRef(0);

  const showToast = useCallback((message: string, type: ToastType = 'success', duration?: number) => {
    const id = `toast-${Date.now()}-${counterRef.current++}`;
    const ms = duration ?? DEFAULT_DURATIONS[type];

    setToasts(prev => {
      const next = [...prev, { id, message, type, duration: ms }];
      return next.slice(-5);
    });

    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, ms);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  useEffect(() => {
    const handleAppError = (event: Event) => {
      const customEvent = event as CustomEvent<{ message?: string }>;
      showToast(customEvent.detail?.message || 'Unexpected application error.', 'error');
    };

    window.addEventListener(APP_ERROR_EVENT, handleAppError);
    return () => window.removeEventListener(APP_ERROR_EVENT, handleAppError);
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};
